import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE } from '../../shared/Constants';

interface ShareInfo {
  share_id: string;
  title: string;
  access: string;
  allowed_emails?: string[];
  created_at: string;
}

interface SharedMessage {
  role: 'user' | 'assistant';
  content: string | any[];
  references?: any[];
}

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  chatId: string;
  /** When set, shares only these messages instead of the full conversation */
  messagesToShare?: SharedMessage[];
  /** Index of the assistant message in messageHistory (stable across reloads) */
  messageIndex?: number;
}

// Backend stores created_at as a naive UTC isoformat string; render it in the
// user's locale (assume UTC when no timezone is present).
const formatShareDate = (iso?: string): string => {
  if (!iso) return '';
  try {
    const normalized = /[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z';
    const d = new Date(normalized);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const ShareModal: React.FC<ShareModalProps> = ({ open, onClose, chatId, messagesToShare, messageIndex }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [shares, setShares] = useState<ShareInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // New share form state
  const [newAccess, setNewAccess] = useState<'public' | 'private'>('public');
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [showNewForm, setShowNewForm] = useState(false);

  const [sendEmail, setSendEmail] = useState(true);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editAccess, setEditAccess] = useState<'public' | 'private'>('public');
  const [editEmails, setEditEmails] = useState<string[]>([]);
  const [editEmailInput, setEditEmailInput] = useState('');
  const [saving, setSaving] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return {
      'Authorization': `Auth ${token}`,
      'Content-Type': 'application/json',
    };
  }, [getAccessTokenSilently]);

  const shareType = messagesToShare ? 'message' : 'conversation';

  const fetchShares = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      let url = `${API_BASE}/api/chat/shares?chat_id=${encodeURIComponent(chatId)}&share_type=${shareType}`;
      if (messageIndex !== undefined) {
        url += `&message_index=${messageIndex}`;
      }
      const res = await fetch(url, { headers });
      if (!res.ok) return;
      const data = await res.json();
      const sharesList = data.shares.map((s: any) => ({
        share_id: s.share_id,
        title: s.title,
        access: s.access || 'public',
        allowed_emails: s.allowed_emails || [],
        created_at: s.created_at,
      }));
      setShares(sharesList);
      if (sharesList.length === 0) {
        setShowNewForm(true);
      }
    } catch (err) {
      console.error('Failed to fetch shares:', err);
      setShowNewForm(true);
    }
    setLoading(false);
  }, [authHeaders, chatId, shareType, messageIndex]);

  useEffect(() => {
    if (open && chatId) {
      // Reset form state on every open
      setEmails([]);
      setEmailInput('');
      setNewAccess('public');
      setEditingId(null);
      setShowNewForm(false);
      fetchShares();
    }
  }, [open, chatId, fetchShares]);

  const handleCreate = async () => {
    if (creating) return;
    if (newAccess === 'private' && emails.length === 0) return;

    setCreating(true);
    try {
      const headers = await authHeaders();
      const body: any = { access: newAccess };
      if (newAccess === 'private') {
        body.allowed_emails = emails;
        body.send_email = sendEmail;
      }
      if (messagesToShare) {
        body.messages = messagesToShare;
        if (messageIndex !== undefined) {
          body.message_index = messageIndex;
        }
      }
      const res = await fetch(`${API_BASE}/api/chat/${chatId}/share`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to create share');

      const data = await res.json();
      const shareUrl = `${window.location.origin}/shared/${data.share_id}`;
      try { await navigator.clipboard.writeText(shareUrl); } catch { /* clipboard not available */ }

      setCopiedId(data.share_id);
      setTimeout(() => setCopiedId(null), 3000);
      setShowNewForm(false);
      setEmails([]);
      setEmailInput('');
      setNewAccess('public');

      await fetchShares();
    } catch (err) {
      console.error('Create share failed:', err);
    }
    setCreating(false);
  };

  const handleRevoke = async (shareId: string) => {
    try {
      const headers = await authHeaders();
      await fetch(`${API_BASE}/api/shared/${shareId}/revoke`, {
        method: 'POST',
        headers,
      });
      setShares(prev => prev.filter(s => s.share_id !== shareId));
    } catch (err) {
      console.error('Revoke failed:', err);
    }
  };

  const handleCopy = async (shareId: string) => {
    const url = `${window.location.origin}/shared/${shareId}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(shareId);
    setTimeout(() => setCopiedId(null), 3000);
  };

  const handleAddEmail = (input: string, setInput: (v: string) => void, list: string[], setList: (v: string[]) => void) => {
    const email = input.trim().toLowerCase();
    if (email && email.includes('@') && !list.includes(email)) {
      setList([...list, email]);
      setInput('');
    }
  };

  const handleRemoveEmail = (email: string, list: string[], setList: (v: string[]) => void) => {
    setList(list.filter(e => e !== email));
  };

  const handleStartEdit = (share: ShareInfo) => {
    setEditingId(share.share_id);
    setEditAccess(share.access as 'public' | 'private');
    setEditEmails(share.allowed_emails || []);
    setEditEmailInput('');
  };

  const handleSaveEdit = async () => {
    if (!editingId || saving) return;
    if (editAccess === 'private' && editEmails.length === 0) return;

    setSaving(true);
    try {
      const headers = await authHeaders();
      const body: any = { access: editAccess };
      if (editAccess === 'private') {
        body.allowed_emails = editEmails;
      }
      await fetch(`${API_BASE}/api/shared/${editingId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      });
      setEditingId(null);
      await fetchShares();
    } catch (err) {
      console.error('Update failed:', err);
    }
    setSaving(false);
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{messagesToShare ? 'Compartilhar resposta' : 'Compartilhar conversa'}</ModalTitle>
          <CloseButton onClick={onClose}><CloseIcon fontSize="small" /></CloseButton>
        </ModalHeader>

        <ModalBody>
          {loading ? (
            <LoadingText>Carregando...</LoadingText>
          ) : (
            <>
              {/* Existing shares */}
              {shares.length > 0 && (
                <Section>
                  <SectionTitle>Links ativos</SectionTitle>
                  {shares.map(share => (
                    <ShareCard key={share.share_id}>
                      {editingId === share.share_id ? (
                        /* Edit mode */
                        <EditForm>
                          <AccessToggle>
                            <AccessOption
                              $active={editAccess === 'public'}
                              onClick={() => setEditAccess('public')}
                            >
                              <PublicIcon fontSize="small" /> Publico
                            </AccessOption>
                            <AccessOption
                              $active={editAccess === 'private'}
                              onClick={() => setEditAccess('private')}
                            >
                              <LockIcon fontSize="small" /> Privado
                            </AccessOption>
                          </AccessToggle>

                          {editAccess === 'private' && (
                            <EmailSection>
                              <EmailInputRow>
                                <EmailInput
                                  type="email"
                                  placeholder="email@exemplo.com"
                                  value={editEmailInput}
                                  onChange={e => setEditEmailInput(e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleAddEmail(editEmailInput, setEditEmailInput, editEmails, setEditEmails);
                                    }
                                  }}
                                />
                                <AddEmailButton
                                  onClick={() => handleAddEmail(editEmailInput, setEditEmailInput, editEmails, setEditEmails)}
                                  disabled={!editEmailInput.includes('@')}
                                >
                                  <AddIcon fontSize="small" />
                                </AddEmailButton>
                              </EmailInputRow>
                              <EmailChips>
                                {editEmails.map(email => (
                                  <EmailChip key={email}>
                                    {email}
                                    <RemoveChip onClick={() => handleRemoveEmail(email, editEmails, setEditEmails)}>x</RemoveChip>
                                  </EmailChip>
                                ))}
                              </EmailChips>
                            </EmailSection>
                          )}

                          <EditActions>
                            <SaveButton onClick={handleSaveEdit} disabled={saving || (editAccess === 'private' && editEmails.length === 0)}>
                              {saving ? 'Salvando...' : 'Salvar'}
                            </SaveButton>
                            <CancelEditButton onClick={() => setEditingId(null)}>Cancelar</CancelEditButton>
                          </EditActions>
                        </EditForm>
                      ) : (
                        /* View mode */
                        <>
                          <ShareCardTop>
                            <ShareAccessBadge $access={share.access}>
                              {share.access === 'private' ? <LockIcon style={{ fontSize: '0.85rem' }} /> : <PublicIcon style={{ fontSize: '0.85rem' }} />}
                              {share.access === 'private' ? 'Privado' : 'Publico'}
                            </ShareAccessBadge>
                            <ShareUrl>{window.location.origin}/shared/{share.share_id}</ShareUrl>
                          </ShareCardTop>
                          {share.created_at && (
                            <ShareCardMeta>Criado em {formatShareDate(share.created_at)}</ShareCardMeta>
                          )}
                          {share.access === 'private' && share.allowed_emails && share.allowed_emails.length > 0 && (
                            <AllowedEmailsList>
                              {share.allowed_emails.map(e => <EmailChipSmall key={e}>{e}</EmailChipSmall>)}
                            </AllowedEmailsList>
                          )}
                          <ShareCardActions>
                            <ActionBtn onClick={() => handleCopy(share.share_id)} title="Copiar link">
                              {copiedId === share.share_id
                                ? <><CheckIcon fontSize="small" style={{ color: '#16a34a' }} /> Copiado!</>
                                : <><ContentCopyIcon fontSize="small" /> Copiar</>
                              }
                            </ActionBtn>
                            <ActionBtn onClick={() => handleStartEdit(share)} title="Editar acesso">
                              Editar
                            </ActionBtn>
                            <ActionBtnDanger onClick={() => handleRevoke(share.share_id)} title="Revogar link">
                              <DeleteOutlineIcon fontSize="small" /> Revogar
                            </ActionBtnDanger>
                          </ShareCardActions>
                        </>
                      )}
                    </ShareCard>
                  ))}
                </Section>
              )}

              {/* Create new share */}
              {!showNewForm ? (
                <CreateButton onClick={() => setShowNewForm(true)}>
                  + Criar novo link de compartilhamento
                </CreateButton>
              ) : (
                <Section>
                  <SectionTitle>Novo link</SectionTitle>
                  <AccessToggle>
                    <AccessOption
                      $active={newAccess === 'public'}
                      onClick={() => setNewAccess('public')}
                    >
                      <PublicIcon fontSize="small" /> Publico
                    </AccessOption>
                    <AccessOption
                      $active={newAccess === 'private'}
                      onClick={() => setNewAccess('private')}
                    >
                      <LockIcon fontSize="small" /> Privado
                    </AccessOption>
                  </AccessToggle>

                  <AccessDescription>
                    {newAccess === 'public'
                      ? 'Qualquer pessoa com o link pode visualizar esta conversa.'
                      : 'Apenas os emails abaixo podem visualizar (requer login).'
                    }
                  </AccessDescription>

                  {newAccess === 'private' && (
                    <EmailSection>
                      <EmailInputRow>
                        <EmailInput
                          type="email"
                          placeholder="email@exemplo.com"
                          value={emailInput}
                          onChange={e => setEmailInput(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddEmail(emailInput, setEmailInput, emails, setEmails);
                            }
                          }}
                        />
                        <AddEmailButton
                          onClick={() => handleAddEmail(emailInput, setEmailInput, emails, setEmails)}
                          disabled={!emailInput.includes('@')}
                        >
                          <AddIcon fontSize="small" />
                        </AddEmailButton>
                      </EmailInputRow>
                      <EmailChips>
                        {emails.map(email => (
                          <EmailChip key={email}>
                            {email}
                            <RemoveChip onClick={() => handleRemoveEmail(email, emails, setEmails)}>x</RemoveChip>
                          </EmailChip>
                        ))}
                      </EmailChips>
                      <SendEmailToggle>
                        <input
                          type="checkbox"
                          id="send-email-toggle"
                          checked={sendEmail}
                          onChange={e => setSendEmail(e.target.checked)}
                        />
                        <label htmlFor="send-email-toggle">Enviar email de notificação</label>
                      </SendEmailToggle>
                    </EmailSection>
                  )}

                  <CreateActions>
                    <ShareNowButton
                      onClick={handleCreate}
                      disabled={creating || (newAccess === 'private' && emails.length === 0)}
                    >
                      {creating ? 'Criando...' : 'Criar e copiar link'}
                    </ShareNowButton>
                  </CreateActions>
                </Section>
              )}
            </>
          )}
        </ModalBody>
      </Modal>
    </Overlay>,
    document.body
  );
};

export default ShareModal;

// --- Styled Components ---

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.15s ease;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.sidebar_background_solid || '#fff'};
  border-radius: 16px;
  width: 680px;
  max-width: 94vw;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.2s ease;
  @keyframes slideUp { from { transform: translateY(10px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.user_menu_border || '#e5e7eb'};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: ${({ theme }) => theme.font_color};
  font-family: 'Sora', 'Lexend', sans-serif;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  color: ${({ theme }) => theme.action_icon_color || '#6b7280'};
  display: flex;
  transition: background 0.15s;
  &:hover { background: ${({ theme }) => theme.hover_overlay_light || '#f3f4f6'}; }
`;

const ModalBody = styled.div`
  padding: 16px 24px 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LoadingText = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.action_icon_color || '#6b7280'};
  padding: 24px 0;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.action_icon_color || '#6b7280'};
`;

const ShareCard = styled.div`
  border: 1px solid ${({ theme }) => theme.user_menu_border || '#e5e7eb'};
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ShareCardTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ShareCardMeta = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.action_icon_color || '#6b7280'};
`;

const ShareAccessBadge = styled.span<{ $access: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  width: fit-content;
  color: ${({ $access }) => $access === 'private' ? '#b45309' : '#047857'};
  background: ${({ $access }) => $access === 'private' ? '#fef3c7' : '#d1fae5'};
`;

const ShareUrl = styled.code`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.action_icon_color || '#6b7280'};
  word-break: break-all;
  font-family: monospace;
`;

const AllowedEmailsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const EmailChipSmall = styled.span`
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.hover_overlay_light || '#f3f4f6'};
  color: ${({ theme }) => theme.font_color};
`;

const ShareCardActions = styled.div`
  display: flex;
  gap: 8px;
  border-top: 1px solid ${({ theme }) => theme.user_menu_border || '#e5e7eb'};
  padding-top: 10px;
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.user_menu_border || '#d1d5db'};
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.82rem;
  font-weight: 500;
  color: ${({ theme }) => theme.font_color};
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  font-family: inherit;
  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light || '#f3f4f6'};
    border-color: ${({ theme }) => theme.font_color || '#9ca3af'};
  }
`;

const ActionBtnDanger = styled(ActionBtn)`
  color: #dc2626;
  border-color: #fecaca;
  &:hover {
    background: #fef2f2;
    border-color: #dc2626;
  }
`;

const AccessToggle = styled.div`
  display: flex;
  gap: 0;
  border: 1px solid ${({ theme }) => theme.user_menu_border || '#d1d5db'};
  border-radius: 10px;
  overflow: hidden;
  width: fit-content;
`;

const AccessOption = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  font-size: 0.88rem;
  font-weight: ${({ $active }) => $active ? 600 : 400};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  font-family: inherit;
  background: ${({ $active, theme }) => $active ? (theme.blue_color || '#4f46e5') : 'transparent'};
  color: ${({ $active, theme }) => $active ? '#fff' : (theme.font_color || '#374151')};
  &:hover {
    background: ${({ $active, theme }) => $active ? (theme.blue_color || '#4f46e5') : (theme.hover_overlay_light || '#f3f4f6')};
  }
`;

const AccessDescription = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.action_icon_color || '#6b7280'};
  line-height: 1.4;
`;

const EmailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmailInputRow = styled.div`
  display: flex;
  gap: 8px;
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.user_menu_border || '#d1d5db'};
  border-radius: 8px;
  font-size: 0.9rem;
  font-family: inherit;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.blue_color || '#4f46e5'};
  }
`;

const AddEmailButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.user_menu_border || '#d1d5db'};
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  transition: background 0.15s;
  &:hover:not(:disabled) { background: ${({ theme }) => theme.hover_overlay_light || '#f3f4f6'}; }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
`;

const EmailChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const EmailChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.hover_overlay_light || '#f3f4f6'};
  color: ${({ theme }) => theme.font_color};
`;

const RemoveChip = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.action_icon_color || '#9ca3af'};
  padding: 0 2px;
  font-weight: 700;
  line-height: 1;
  &:hover { color: #dc2626; }
`;

const SendEmailToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.font_color};
  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: ${({ theme }) => theme.blue_color || '#4f46e5'};
  }
  label {
    cursor: pointer;
    user-select: none;
  }
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border: 2px dashed ${({ theme }) => theme.user_menu_border || '#d1d5db'};
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.action_icon_color || '#6b7280'};
  font-family: inherit;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  &:hover {
    border-color: ${({ theme }) => theme.blue_color || '#4f46e5'};
    color: ${({ theme }) => theme.blue_color || '#4f46e5'};
    background: ${({ theme }) => theme.hover_overlay_light || '#f9fafb'};
  }
`;

const CreateActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ShareNowButton = styled.button`
  flex: 1;
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  background: ${({ theme }) => theme.blue_color || '#4f46e5'};
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s, transform 0.1s;
  &:hover:not(:disabled) { background: #4338ca; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CancelEditButton = styled.button`
  padding: 10px 16px;
  border: 1px solid ${({ theme }) => theme.user_menu_border || '#d1d5db'};
  border-radius: 10px;
  background: transparent;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  font-family: inherit;
  transition: background 0.15s;
  &:hover { background: ${({ theme }) => theme.hover_overlay_light || '#f3f4f6'}; }
`;

const EditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EditActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SaveButton = styled(ShareNowButton)`
  flex: none;
`;
